import { useEffect } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { getLoginUrl } from "@/const";

export type VIPTier = "vault" | "ops" | "enterprise" | "legacy" | null;

/**
 * VIP Gate Hook
 * Checks if the current user has an active VIP tier.
 * Redirects to login if unauthenticated, or to /vip-upgrade if not a VIP member.
 *
 * Usage: call at the top of any VIP page component.
 * Returns { isVIP, tier, loading } so the page can render a skeleton while checking.
 */
export function useVIPAccess() {
  const [, setLocation] = useLocation();
  const { data: me, isLoading } = trpc.auth.me.useQuery();

  const tier = (me?.vipTier ?? null) as VIPTier;
  const isVIP = tier !== null;

  useEffect(() => {
    if (isLoading) return;

    // Not logged in → send to login
    if (!me) {
      window.location.href = getLoginUrl();
      return;
    }

    // Logged in but no VIP tier → send to upgrade page (admins bypass)
    if (!isVIP && me?.role !== "admin") {
      setLocation("/vip-upgrade");
    }
  }, [isLoading, me, isVIP, setLocation]);

  return { isVIP, tier, loading: isLoading, user: me };
}
